import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import logo from '../logo.svg';
import { FiMail, FiArrowRight, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      // Backend always returns a generic success response regardless of
      // whether the email is registered — don't branch on the body here,
      // that would defeat the anti-enumeration protection server-side.
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || t('forgotPasswordErrorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        .lp-root {
          min-height: 100vh; position: relative; overflow: hidden;
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: #06060E; color: #F2F0FA;
        }
        .lp-photo-bg {
          position: absolute; inset: 0; z-index: 0;
          background:
            linear-gradient(180deg, rgba(6,6,14,0.80) 0%, rgba(6,6,14,0.25) 24%, rgba(6,6,14,0.35) 70%, rgba(6,6,14,0.88) 100%),
            linear-gradient(100deg, rgba(6,6,14,0.55) 0%, rgba(6,6,14,0.35) 50%, rgba(6,6,14,0.6) 100%),
            url('/images/login/kardian-showroom.jpg') center 40% / cover no-repeat;
        }
        .lp-nav { position: absolute; top: 0; left: 0; right: 0; padding: 24px 40px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
        @media (max-width: 640px) { .lp-nav { padding: 18px 22px; } }
        .lp-nav-brand { display: flex; align-items: center; gap: 10px; }
        .lp-nav-brand img { height: 32px; width: auto; filter: brightness(0) invert(1); }
        .lp-nav-brand-name { font-family: 'Outfit', sans-serif; font-size: 19px; font-weight: 800; color: #fff; letter-spacing: -0.02em; }
        .lp-body { position: absolute; inset: 0; padding: 100px 60px 50px; display: flex; align-items: center; justify-content: center; z-index: 5; }
        @media (max-width: 900px) { .lp-body { padding: 90px 22px 30px; } }
        .lp-card { width: 100%; max-width: 420px; background: rgba(15,13,26,0.58); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.14); border-radius: 26px; padding: 32px; box-shadow: 0 24px 60px -16px rgba(0,0,0,0.55); }
        .lp-card-title { font-size: 20px; font-weight: 800; letter-spacing: -0.01em; color: #fff; }
        .lp-card-subtitle { margin-top: 6px; font-size: 13.5px; line-height: 1.55; color: rgba(242,240,250,0.62); }
        .lp-card-body { margin-top: 22px; display: flex; flex-direction: column; gap: 16px; }
        .lp-field { display: flex; flex-direction: column; gap: 0; }
        .lp-label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(242,240,250,0.55); margin-bottom: 7px; font-family: inherit; }
        .lp-input-wrap { position: relative; }
        .lp-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(242,240,250,0.45); pointer-events: none; display: flex; }
        .lp-input { width: 100%; padding: 13px 14px 13px 42px; font-size: 14.5px; border: 1.5px solid rgba(255,255,255,0.16); border-radius: 15px; background: rgba(255,255,255,0.06); color: #fff; outline: none; transition: border-color 0.2s, box-shadow 0.2s, background 0.2s; appearance: none; font-family: inherit; }
        .lp-input::placeholder { color: rgba(242,240,250,0.4); }
        .lp-input:focus { border-color: #8B7FF0; box-shadow: 0 0 0 4px rgba(139,127,240,0.2); background: rgba(255,255,255,0.1); }
        .lp-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .lp-error { display: flex; align-items: flex-start; gap: 10px; background: rgba(225,72,72,0.16); border: 1px solid rgba(225,72,72,0.35); border-radius: 12px; padding: 11px 13px; font-size: 13px; color: #FF9E9E; line-height: 1.5; }
        .lp-success { display: flex; align-items: flex-start; gap: 10px; background: rgba(139,127,240,0.14); border: 1px solid rgba(139,127,240,0.35); border-radius: 12px; padding: 13px; font-size: 13.5px; color: #C9C2FF; line-height: 1.55; }
        .lp-success svg { flex-shrink: 0; margin-top: 1px; }
        .lp-btn { width: 100%; padding: 14px 20px; border-radius: 15px; background: linear-gradient(135deg, #4F3EE0 0%, #FF6B4A 100%); color: #fff; font-weight: 700; font-size: 15px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: transform 0.15s, box-shadow 0.15s; box-shadow: 0 10px 26px -6px rgba(255,107,74,0.45); font-family: inherit; }
        .lp-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 30px -6px rgba(255,107,74,0.55); }
        .lp-btn:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }
        .lp-links { display: flex; justify-content: center; font-size: 12.5px; color: rgba(242,240,250,0.6); margin-top: 4px; }
        .lp-links a { color: inherit; text-decoration: none; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 500; }
        .lp-links a:hover { color: #FFB37A; }
        .lp-footer { position: absolute; bottom: 22px; left: 40px; right: 40px; text-align: center; font-size: 12px; color: rgba(242,240,250,0.5); letter-spacing: 0.04em; z-index: 5; }
        .lp-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: lp-spin 0.7s linear infinite; }
        @keyframes lp-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="lp-root">
        <div className="lp-photo-bg" />

        <div className="lp-nav">
          <div className="lp-nav-brand">
            <img src={logo} alt="Rentora" />
            <span className="lp-nav-brand-name">Rentora</span>
          </div>
        </div>

        <div className="lp-body">
          <div className="lp-card">
            <div className="lp-card-title">{t('forgotPasswordTitle')}</div>
            <div className="lp-card-subtitle">{t('forgotPasswordSubtitle')}</div>

            <div className="lp-card-body">
              {error && (
                <div className="lp-error">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {sent ? (
                <>
                  <div className="lp-success">
                    <FiCheckCircle size={17} />
                    {t('forgotPasswordSuccessMessage')}
                  </div>
                  <div className="lp-links">
                    <a
                      href="/login"
                      onClick={(e) => { e.preventDefault(); navigate('/login'); }}
                    >
                      <FiArrowLeft size={13} /> {t('forgotPasswordBackToLogin')}
                    </a>
                  </div>
                </>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="fp-email">{t('forgotPasswordEmailLabel')}</label>
                    <div className="lp-input-wrap">
                      <span className="lp-input-icon"><FiMail size={15} /></span>
                      <input
                        id="fp-email"
                        className="lp-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('forgotPasswordEmailPlaceholder')}
                        required
                        disabled={loading}
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                  </div>

                  <button className="lp-btn" type="submit" disabled={loading}>
                    {loading
                      ? <><div className="lp-spinner" /> {t('loading')}...</>
                      : <>{t('forgotPasswordSubmitButton')} <FiArrowRight size={14} /></>
                    }
                  </button>

                  <div className="lp-links">
                    <a
                      href="/login"
                      onClick={(e) => { e.preventDefault(); navigate('/login'); }}
                    >
                      <FiArrowLeft size={13} /> {t('forgotPasswordBackToLogin')}
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="lp-footer">© Rentora {new Date().getFullYear()}</div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
